import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { supabase } from '../../../shared/lib/supabase'
import MFAVerify from './MFAVerify'

const IdleTimeoutContext = createContext(null)

// 4 hours in milliseconds
const IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000

// Check interval (every minute)
const CHECK_INTERVAL_MS = 60 * 1000

// Storage key
const LAST_ACTIVITY_KEY = 'ssa-last-activity'
const MFA_VERIFIED_KEY = 'ssa-mfa-verified-at'

export function IdleTimeoutProvider({ children }) {
  const { user, signOut } = useAuth()
  const [showMFAVerify, setShowMFAVerify] = useState(false)
  const [hasMFA, setHasMFA] = useState(false)
  const lastActivityRef = useRef(Date.now())
  const checkIntervalRef = useRef(null)

  // Update last activity time
  const updateActivity = useCallback(() => {
    const now = Date.now()
    lastActivityRef.current = now
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
  }, [])

  // Check if user has MFA enabled
  const checkMFAStatus = useCallback(async () => {
    if (!user) {
      setHasMFA(false)
      return
    }

    try {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) {
        console.error('Error checking MFA status:', error)
        setHasMFA(false)
        return
      }

      const hasVerifiedTotp = data.totp?.some(f => f.status === 'verified')
      setHasMFA(hasVerifiedTotp)
    } catch (err) {
      console.error('Error checking MFA status:', err)
      setHasMFA(false)
    }
  }, [user])

  // Check if MFA re-verification is needed
  const checkIdleTimeout = useCallback(() => {
    if (!user || !hasMFA) return

    const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10)
    const lastMFAVerify = parseInt(localStorage.getItem(MFA_VERIFIED_KEY) || '0', 10)
    const now = Date.now()

    // Check if user has been idle for more than the timeout
    const idleTime = now - lastActivity
    const timeSinceMFAVerify = now - lastMFAVerify

    // If idle for 4+ hours AND it's been 4+ hours since last MFA verification
    if (idleTime >= IDLE_TIMEOUT_MS && timeSinceMFAVerify >= IDLE_TIMEOUT_MS) {
      import('../../../shared/lib/auditLog').then(({ logAuditEvent }) =>
        logAuditEvent({
          category: 'system',
          action: 'session.idle_timeout',
          description: 'Session idle timeout — MFA re-verification required',
          metadata: { idleMinutes: Math.floor(idleTime / 60000) }
        })
      )
      setShowMFAVerify(true)
    }
  }, [user, hasMFA])

  // Set up activity listeners
  useEffect(() => {
    if (!user) return

    // Initialize last activity
    updateActivity()

    // Activity events to track
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']

    // Throttled activity handler
    let throttleTimeout = null
    const throttledUpdate = () => {
      if (throttleTimeout) return
      throttleTimeout = setTimeout(() => {
        updateActivity()
        throttleTimeout = null
      }, 1000) // Update at most once per second
    }

    // Add listeners
    events.forEach(event => {
      document.addEventListener(event, throttledUpdate, { passive: true })
    })

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledUpdate)
      })
      if (throttleTimeout) clearTimeout(throttleTimeout)
    }
  }, [user, updateActivity])

  // Check MFA status when user changes
  useEffect(() => {
    checkMFAStatus()
  }, [user, checkMFAStatus])

  // Set up idle check interval
  useEffect(() => {
    if (!user || !hasMFA) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
      return
    }

    // Check immediately
    checkIdleTimeout()

    // Set up interval
    checkIntervalRef.current = setInterval(checkIdleTimeout, CHECK_INTERVAL_MS)

    // Also check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkIdleTimeout()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user, hasMFA, checkIdleTimeout])

  // Handle successful MFA verification
  const handleMFAVerified = () => {
    setShowMFAVerify(false)
    updateActivity()
    // MFA verified timestamp is set in MFAVerify component
  }

  // Handle cancel/sign out from MFA modal
  const handleMFACancel = async () => {
    setShowMFAVerify(false)
    await signOut()
  }

  // Refresh MFA status (called after enabling/disabling MFA)
  const refreshMFAStatus = useCallback(() => {
    checkMFAStatus()
  }, [checkMFAStatus])

  const value = {
    hasMFA,
    refreshMFAStatus
  }

  return (
    <IdleTimeoutContext.Provider value={value}>
      {children}
      {showMFAVerify && hasMFA && (
        <MFAVerify
          isIdleTimeout={true}
          onVerified={handleMFAVerified}
          onCancel={handleMFACancel}
        />
      )}
    </IdleTimeoutContext.Provider>
  )
}

export function useIdleTimeout() {
  const context = useContext(IdleTimeoutContext)
  if (!context) {
    throw new Error('useIdleTimeout must be used within an IdleTimeoutProvider')
  }
  return context
}

export default IdleTimeoutProvider
